/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ActionButtonsSection — Accordion: configures the buttons a custom layout
 * renders where the template says {{ actions }}.
 *
 * Buttons are configuration rather than markup so their destination cannot be
 * influenced by anything a visitor sends, and so a builder does not hand-write
 * <button> tags.
 *
 * The URL warning here mirrors the client's scheme allowlist. The client is the
 * real control — it drops an unsafe button rather than rendering it — but a
 * builder should learn that `javascript:` is refused while typing, not by
 * wondering why their button vanished from the page.
 *
 * `{{ table.field }}` inside a label or URL is substituted per record at render
 * time, with URL values escaped, so one button definition serves every record.
 */

import React, { useState } from 'react';
import ConditionRuleDialog from './ConditionRuleDialog';
import Hint from './Hint';
import RelatedFieldRuleDialog, { relatedChoices } from './RelatedFieldRuleDialog';
import LookupFields from './LookupFields';
import SuccessRevealDialog from './SuccessRevealDialog';
import { CONDITION_OPS, OPS_WITH_VALUE, conditionValue,
         allowedValuesOf, unknownConditionValues } from './conditionOps';

const SAFE_URL_RE = /^(https?:|mailto:|tel:|\/(?!\/))/i;

function newButton() {
  return {
    // A readable default the builder can rename — it is typed into the
    // template as {{ actions.<id> }}, so an opaque generated id would be
    // unusable.
    id: 'button',
    label: 'Button',
    style: 'filled',
    icon: '',
    placement: ['detail'],
    order: 0,
    action: { type: 'redirect', target: 'url', url: '' },
  };
}

/**
 * Value sources a JSON entry may draw on — the same vocabulary as a column.
 *
 * `hint` says what actually lands in the column, because "A field from this
 * record" does not tell a builder whether they get the field's name or its
 * value. Shown under the picker once a source is chosen.
 */
const JSON_SOURCES = [
  { key: 'form',         label: 'Answer from the form',
    hint: 'What the visitor typed into that question.' },
  { key: 'record_id',    label: "This record's id",
    hint: 'The id of the record the button was clicked on.' },
  { key: 'record_field', label: 'A field from this record',
    hint: "The value of that column on this record, read from the database — not what the browser sent." },
  { key: 'user_id',      label: "The signed-in person's id",
    hint: 'Taken from the session, so it cannot be faked.' },
  { key: 'const',        label: 'A fixed value',
    hint: 'The same value on every submission — useful for marking where a registration came from.' },
  { key: 'now',          label: 'The time this is saved',
    hint: 'The moment the write lands, taken from the server — not from the visitor\'s device.' },
  { key: 'payload',      label: 'Value sent by the page',
    hint: 'A value the page sends with the click, under this name — for details the visitor '
      + 'types into the page itself rather than a Form Builder form. The visitor controls it, '
      + 'so never use it for an amount or a status.' },
];

/* The name the page sends a value under. Typed, because the page is code the
 * builder's developer wrote — there is no list of names to pick from. */
const PAYLOAD_PLACEHOLDER = 'name the page sends, e.g. email';

/**
 * Turns a form question's label into a key anyone can read later.
 *
 * The stored key is what someone querying the JSON months from now has to make
 * sense of, so it comes from the human label — "T-shirt size" becomes
 * `tshirt_size` — rather than from the form's internal id (`p_size`), which
 * means nothing outside Form Builder. Always editable.
 */
function suggestKey(label, fallback) {
  const base = String(label || fallback || '').trim().toLowerCase();
  const key = base
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return key || String(fallback || '');
}

/**
 * Extra values saved into a JSON column, as key/value pairs.
 *
 * WHY IT EXISTS. A form question with no column of its own is validated and
 * then dropped — the answer reaches the server and goes nowhere. This gives
 * those answers a home, and takes any other value source too, so a builder can
 * record whatever they choose rather than only the leftovers.
 *
 * NOTHING IS IMPLICIT. An entry is here because someone added it. There is no
 * "capture everything else" mode: storing data nobody chose to store is how a
 * form quietly becomes a liability.
 *
 * LAYOUT. This panel is the narrow half of a split pane, so each entry stacks —
 * key on its own line, then source, then the detail the source needs — instead
 * of a row of three controls that would wrap into an unreadable ladder.
 */
function JsonFieldMap({ op, columns, formFields, recordColumns, sources, idPrefix, onChange }) {
  const spec = op.json_fields || {};
  const map = spec.map || {};
  const entries = Object.keys(map);

  // What the builder has typed, before it is safe to store. A JSON object
  // cannot hold the same name twice, so committing a rename onto an existing
  // name would destroy the other entry outright — there is no state in which
  // both exist to warn about. So a colliding or empty name is held here,
  // shown back with the reason, and simply not written until it is usable.
  const [draft, setDraft] = React.useState({});
  const column = spec.column || (columns.length === 1 ? columns[0] : '');

  const write = (nextMap, nextColumn) => {
    const cleaned = Object.keys(nextMap).length ? nextMap : undefined;
    onChange({
      json_fields: cleaned
        ? { ...(nextColumn ? { column: nextColumn } : {}), map: cleaned }
        : undefined,
    });
  };

  const readEntry = (key) => {
    const rule = map[key];
    if (typeof rule === 'string' && rule.startsWith('@form.')) {
      return { source: 'form', detail: rule.slice('@form.'.length) };
    }
    if (typeof rule === 'string' && rule.startsWith('@payload.')) {
      return { source: 'payload', detail: rule.slice('@payload.'.length) };
    }
    if (rule && 'const' in rule) return { source: 'const', detail: rule.const };
    if (rule && rule.from === 'record_field') return { source: 'record_field', detail: rule.field || '' };
    if (rule && rule.from === 'user_field') return { source: 'user_field', detail: rule.field || '' };
    if (rule && rule.from) return { source: rule.from, detail: '' };
    return { source: '', detail: '' };
  };

  const ruleFor = (source, detail) => {
    if (source === 'form') return `@form.${detail || ''}`;
    if (source === 'payload') return `@payload.${detail || ''}`;
    if (source === 'const') return { const: detail };
    if (source === 'record_field') return { from: 'record_field', field: detail || '' };
    if (source === 'user_field') return { from: 'user_field', field: detail || '' };
    return { from: source };
  };

  // Keys are rebuilt in order so renaming one does not send it to the end.
  const setEntry = (oldKey, newKey, source, detail) => {
    const next = {};
    entries.forEach((k) => {
      if (k === oldKey) {
        if (newKey !== null) next[newKey] = ruleFor(source, detail);
      } else {
        next[k] = map[k];
      }
    });
    write(next, column);
  };

  const shownKey = (key, position) =>
    (position in draft ? draft[position] : key);

  const problemWith = (key, position) => {
    const typed = shownKey(key, position);
    if (!typed) return 'Name this, or it will not be saved.';
    if (entries.some((k, i) => k === typed && i !== position)) {
      return 'Already used above — pick another name.';
    }
    return null;
  };

  // Held rather than written when it would collide or blank the entry.
  const changeKey = (key, position, typed, source, detail) => {
    setDraft({ ...draft, [position]: typed });
    const collides = entries.some((k, i) => k === typed && i !== position);
    if (typed && !collides) setEntry(key, typed, source, detail);
  };

  const addEntry = () => {
    // Seeded from the first unmapped question when there is one, because that
    // is overwhelmingly what this is for.
    const used = new Set(entries.map(k => readEntry(k).detail));
    const spare = (formFields || []).find(f => !used.has(f.id));
    const key = spare ? suggestKey(spare.label, spare.id) : '';
    const next = { ...map };
    next[key && !(key in map) ? key : ''] = spare ? `@form.${spare.id}` : { const: '' };
    write(next, column);
  };

  const needsText = s => s === 'const' || s === 'record_field' || s === 'user_field' || s === 'payload';

  return (
    <div className="mt-2 pt-2 border-top">
      <div className="d-flex align-items-center gap-2 mb-1">
        <span className="small fw-semibold">Also save into</span>
        {columns.length > 1 ? (
          <select
            className="form-select form-select-sm" style={{ maxWidth: 150 }}
            aria-label="JSON column"
            value={column}
            onChange={e => write(map, e.target.value)}
          >
            <option value="">— choose a column —</option>
            {columns.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <span className="small">
            <code>{columns[0]}</code>
            <span className="text-muted"> (json column)</span>
          </span>
        )}
      </div>

      {entries.length === 0 && (
        <Hint>
          Nothing saved here yet. Use this for answers with no column of their own —
          they are collected and then dropped otherwise.
        </Hint>
      )}

      {entries.map((key, n) => {
        const { source, detail } = readEntry(key);
        return (
          <div className="mb-2 p-2 rounded border bg-body-tertiary" key={n}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <input
                className={'form-control form-control-sm'
                  + (problemWith(key, n) ? ' is-invalid' : '')}
                aria-label={`JSON key ${n + 1}`}
                placeholder="name it clearly, e.g. tshirt_size"
                value={shownKey(key, n)}
                onChange={e => changeKey(key, n, e.target.value, source, detail)}
              />
              <button
                type="button" className="btn btn-sm btn-outline-danger"
                aria-label={`Remove JSON entry ${n + 1}`}
                onClick={() => setEntry(key, null)}
              >
                ✕
              </button>
            </div>

            {problemWith(key, n) && (
              <div className="small text-danger mb-1">{problemWith(key, n)}</div>
            )}

            <select
              className="form-select form-select-sm mb-1"
              aria-label={`JSON source ${n + 1}`}
              value={source}
              onChange={e => setEntry(key, key, e.target.value, '')}
            >
              <option value="">— not set —</option>
              {sources.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              {source && !sources.some(s => s.key === source) && (
                <option value={source}>{source} (no longer offered)</option>
              )}
            </select>

            {source === 'form' && (
              <select
                className="form-select form-select-sm"
                aria-label={`JSON form field ${n + 1}`}
                value={detail}
                onChange={e => {
                  // Name it after the question when it has no name yet, so
                  // picking a question is enough to store the answer. A key
                  // already typed is left alone — it was chosen deliberately.
                  // Decide on what is DISPLAYED, not on what is stored: a name
                  // the builder has just cleared is still the stored one, and
                  // checking that would leave the field blank — which the
                  // writer then skips, storing nothing at all.
                  const picked = (formFields || []).find(f => f.id === e.target.value);
                  const shown = shownKey(key, n);
                  // A name this panel guessed follows the question; a name the
                  // builder typed does not. Told apart without extra state: if
                  // the current name is still exactly what would have been
                  // suggested for the question it currently points at, nobody
                  // has touched it. Otherwise it was chosen, and stands.
                  const previous = (formFields || []).find(f => f.id === detail);
                  const wasGuessed = !shown
                    || (previous && shown === suggestKey(previous.label, previous.id));
                  const suggestion = picked ? suggestKey(picked.label, picked.id) : '';
                  const nextKey = wasGuessed ? (suggestion || shown) : shown;
                  if (nextKey !== shown) {
                    const rest = { ...draft };
                    delete rest[n];
                    setDraft(rest);
                  }
                  setEntry(key, nextKey, 'form', e.target.value);
                }}
              >
                <option value="">— choose a question —</option>
                {(formFields || []).map(f => (
                  <option key={f.id} value={f.id}>{f.label || f.id}</option>
                ))}
              </select>
            )}

            {source === 'record_field' && (
              <select
                className="form-select form-select-sm"
                aria-label={`JSON record column ${n + 1}`}
                value={detail}
                onChange={e => setEntry(key, key, source, e.target.value)}
              >
                <option value="">— choose a column —</option>
                {(recordColumns || []).map(c => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            )}

            {needsText(source) && source !== 'record_field' && (
              <input
                className="form-control form-control-sm"
                aria-label={`JSON detail ${n + 1}`}
                placeholder={source === 'const' ? 'value'
                  : source === 'payload' ? PAYLOAD_PLACEHOLDER : 'column'}
                value={detail}
                onChange={e => setEntry(key, key, source, e.target.value)}
              />
            )}

            {source && (
              <Hint>
                {(sources.find(s => s.key === source) || {}).hint}
              </Hint>
            )}
          </div>
        );
      })}

      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addEntry}>
        + Add value
      </button>
      <Hint>
        Saved alongside the columns above, never instead of them.
      </Hint>
    </div>
  );
}

/**
 * An explanatory note under a control.
 *
 * The icon is the point: these sit among inputs, and without a consistent mark
 * an explanation reads as another label. `align-items-start` keeps the icon on
 * the first line when the text wraps — which it does often in this panel, since
 * it is the narrow half of a split pane.
 */

/* Not a column name — no column may contain a dot — so it can never collide
 * with a real option in the same dropdown. */
const RELATED_OPTION = '__related__';

export default function ActionButtonsSection({
  config, updateConfig, theme, tableName, writableTables, forms, recordColumns,
  relatedEdges,
}) {
  // Columns of a chosen table, and the questions on a chosen form — so the
  // builder picks from real names instead of typing them.
  const columnsOf = (name) => {
    const t = (writableTables || []).find(x => x.name === name) || {};
    // `columns` is what the table list returns with include_fields=1; `fields`
    // is accepted too so a caller passing a fuller table object still works.
    return t.columns || t.fields || [];
  };
  const formFieldsOf = id =>
    (((forms || []).find(f => String(f.id) === String(id)) || {}).fields_schema || []);
  // Which condition row, if any, is being edited in the related-field dialog.
  const [relatedRule, setRelatedRule] = useState(null);
  // Blank condition rows a builder has opened but not yet filled in, per button.
  // They are held here rather than in the stored config precisely because they
  // are not rules yet: an unfinished rule resolves to nothing and fails closed,
  // which would hide the button on the first click of "+ Add a condition".
  const [pendingRows, setPendingRows] = useState({});
  const hasRelated = relatedChoices(relatedEdges).length > 0;

  const buttons = config?.action_buttons || [];
  const tables = writableTables || [];
  const availableForms = forms || [];

  const write = next => updateConfig({ action_buttons: next });
  const patch = (index, changes) =>
    write(buttons.map((b, i) => (i === index ? { ...b, ...changes } : b)));
  const patchAction = (index, changes) =>
    patch(index, { action: { ...(buttons[index].action || {}), ...changes } });

  // What the visitor is told once the write lands. `message` is a sentence;
  // What the visitor sees on a LATER visit, once they have already completed
  // this button. Separate from on_success because the two are read at different
  // moments: "Thanks, you're in" is right at the click and wrong a week later,
  // when "You are registered for this event" is what the page should say.
  const patchOnCompleted = (index, changes) => {
    const next = { ...(buttons[index].action?.on_completed || {}), ...changes };
    // Dropped entirely when the switch is off, so a button that is not using
    // this carries no config for it at all.
    patchAction(index, {
      on_completed: next.hide === true ? next : undefined,
    });
  };

  // `reveal` is the generated values — a code, a token as a picture — which
  // exist only after the write and cannot reach the visitor any other way.
  const patchOnSuccess = (index, changes) => {
    const next = { ...(buttons[index].action?.on_success || {}), ...changes };
    Object.keys(next).forEach(k => next[k] === undefined && delete next[k]);
    patchAction(index, { on_success: Object.keys(next).length ? next : undefined });
  };

  // Which button's reveal dialog is open. Switching the toggle ON opens it
  // rather than storing an empty reveal: a reveal with nothing chosen would
  // render a modal with nothing in it.
  const [revealDialogFor, setRevealDialogFor] = useState(null);
  // {buttonIndex, opIndex, column} of the condition being edited.
  const [conditionFor, setConditionFor] = useState(null);

  // Where one column's value comes from.
  //
  // 'A field from the signed-in person' is deliberately NOT offered. It reads
  // attributes off ApplicationUser, which holds only id, timestamps, a deleted
  // flag and two foreign keys — nothing worth storing. The person's email and
  // name live on the related auth user, which it cannot reach, so every useful
  // field name silently resolved to null. Reading it back is still supported so
  // an older configuration keeps working.
  const SOURCES = [
    { key: 'form',         label: 'Answer from the form' },
    { key: 'record_id',    label: "This record's id" },
    { key: 'record_field', label: 'A field from this record' },
    { key: 'const',        label: 'A fixed value' },
    { key: 'ref',          label: 'The row created in an earlier step' },
    { key: 'user_id',      label: "The signed-in person's id" },
    { key: 'now',          label: 'The time this is saved' },
    { key: 'lookup',       label: 'A value from another table' },
    { key: 'when',         label: 'A value that depends on a condition' },
    // For a page that collects its own values instead of opening a form. The
    // visitor controls these exactly as they control a form answer, so a value
    // that names a ROW is only safe behind an owner check — see ownerChecksOf.
    { key: 'payload',      label: 'Value sent by the page' },
  ];

  // What a lookup can match against. A lookup is not offered: one hop keeps the
  // configuration readable, and the server refuses a nested one anyway.
  const MATCH_SOURCES = [
    { key: 'record_id',    label: "this record's id" },
    { key: 'record_field', label: 'a field from this record' },
    { key: 'user_id',      label: "the signed-in person's id" },
    { key: 'const',        label: 'a fixed value' },
  ];

  // Columns that may identify a repeat submission. The server refuses to dedupe
  // on anything the visitor controls — otherwise someone sends another person's
  // email as the identifying value and overwrites THEIR row — so the choice is
  // limited here to what is already server-filled. That makes the safe set the
  // only set, rather than a rule to remember.
  function serverColumnsOf(op) {
    const server = op.server_fields || {};
    return Object.keys(server).filter(column => {
      const rule = server[column];
      if (!rule) return false;
      // A FIXED value is excluded. It is server-filled, so the server would
      // accept it — but every submission carries the same value, so it cannot
      // tell one from another. On its own it would match the first row holding
      // that value and overwrite a stranger's row; alongside other columns it
      // narrows nothing. Offering it only invites that mistake.
      return !('const' in rule);
    });
  }

  // Columns the row-by-row mapping offers. A JSON column is deliberately absent:
  // it has its own block below, and the writer applies that block LAST — so a
  // value mapped here as well would be silently overwritten, with nothing on
  // screen to explain where it went. One column, one place to configure it.
  //
  // `id` is absent too: it decides whether the operation adds a row or updates
  // one, which is the "Row to write" control, not a value like any other.
  function mappableColumnsOf(tableName) {
    const json = new Set(jsonColumnsOf(tableName));
    return columnsOf(tableName).filter(c => !json.has(c.name) && c.name !== 'id');
  }

  // A JSON destination needs somewhere to go. The table list carries each
  // column's data_type, so a table with no JSON column simply is not offered
  // the option — rather than accepting a tick that fails on the first click.
  function jsonColumnsOf(tableName) {
    return columnsOf(tableName)
      .filter(c => ['json', 'jsonb'].includes(String(c.data_type || '').toLowerCase()))
      .map(c => c.name);
  }

  function toggleDedupe(buttonIndex, opIndex, column, on) {
    const op = buttons[buttonIndex].action.operations[opIndex];
    const current = op.dedupe_on || [];
    const next = on ? [...current, column] : current.filter(c => c !== column);
    patchOperation(buttonIndex, opIndex, { dedupe_on: next.length ? next : undefined });
  }

  /**
   * The one line a configured condition shows on the card.
   *
   * States the OUTCOME rather than the shape — "waiting list -> PENDING,
   * otherwise CONFIRMED" is what a builder is checking when they scan the card;
   * that it is stored as a test and a list of cases is not.
   */
  function summariseCondition(rule) {
    const cases = (rule && rule.cases) || [];
    if (!cases.length || !rule.test) return 'Not configured yet.';

    const test = rule.test.from === 'lookup'
      ? `${rule.test.field || '?'} from ${rule.test.table || '?'}`
      : (rule.test.field || '?');

    const branches = cases.map(c => {
      const op = (CONDITION_OPS.find(o => o.key === c.op) || {}).label || c.op;
      const value = Array.isArray(c.value) ? c.value.join('/') : c.value;
      const wrote = (c.then || {}).const;
      return `${op}${OPS_WITH_VALUE.includes(c.op) ? ` ${value}` : ''} → ${wrote}`;
    }).join(', ');

    const fallback = ((rule.else || {}).const);
    return `${test}: ${branches}`
      + (fallback === undefined ? '' : `, otherwise ${fallback}`);
  }

  function readMapping(op, column) {
    const v = (op.visitor_fields || {})[column];
    if (typeof v === 'string' && v.startsWith('@form.')) {
      return { source: 'form', value: v.slice('@form.'.length) };
    }
    if (typeof v === 'string' && v.startsWith('@payload.')) {
      return { source: 'payload', value: v.slice('@payload.'.length) };
    }
    const sf = (op.server_fields || {})[column];
    if (sf && 'const' in sf) return { source: 'const', value: sf.const };
    if (sf && sf.from === 'record_id') return { source: 'record_id', value: '' };
    if (sf && sf.from === 'record_field') return { source: 'record_field', value: sf.field || '' };
    if (sf && sf.from === 'user_id') return { source: 'user_id', value: '' };
    if (sf && sf.from === 'now') return { source: 'now', value: '' };
    if (sf && sf.from === 'user_field') return { source: 'user_field', value: sf.field || '' };
    if (sf && sf.from === 'lookup') {
      const matchTo = sf.match_to || {};
      const matchSource = 'const' in matchTo ? 'const' : (matchTo.from || 'record_id');
      return {
        source: 'lookup', value: '',
        lookup: {
          table: sf.table || '',
          matchColumn: sf.match_column || '',
          matchSource,
          matchValue: 'const' in matchTo ? matchTo.const : (matchTo.field || ''),
          field: sf.field || '',
        },
      };
    }
    if (sf && sf.from === 'when') return { source: 'when', value: '', when: sf };
    if (sf && typeof sf.from === 'string' && sf.from.startsWith('@')) {
      return { source: 'ref', value: sf.from === '@' ? '' : sf.from };
    }
    return { source: '', value: '' };
  }

  // A lookup carries four settings rather than one value, so it is patched part
  // by part — the builder fills it in over several interactions and a partial
  // lookup must survive until they finish it.
  function writeLookup(buttonIndex, opIndex, column, changes) {
    const op = buttons[buttonIndex].action.operations[opIndex];
    const existing = (op.server_fields || {})[column] || {};
    const current = readMapping(op, column).lookup
      || { table: '', matchColumn: '', matchSource: 'record_id', matchValue: '', field: '' };
    const next = { ...current, ...changes };

    const matchTo = next.matchSource === 'const'
      ? { const: next.matchValue }
      : next.matchSource === 'record_field'
        ? { from: 'record_field', field: next.matchValue }
        : { from: next.matchSource };

    const server = { ...(op.server_fields || {}) };
    server[column] = {
      ...(existing.from === 'lookup' ? {} : {}),
      from: 'lookup',
      table: next.table,
      match_column: next.matchColumn,
      match_to: matchTo,
      field: next.field,
    };
    patchOperation(buttonIndex, opIndex, { server_fields: server });
  }

  function writeMapping(buttonIndex, opIndex, column, source, value) {
    const op = { ...(buttons[buttonIndex].action.operations[opIndex]) };
    const visitor = { ...(op.visitor_fields || {}) };
    const server = { ...(op.server_fields || {}) };
    delete visitor[column];
    delete server[column];

    // A visitor value and a server value for the same column would be
    // ambiguous, so a column lives in exactly one of the two maps.
    //
    // A source chosen but not yet filled in is STORED, not discarded — the
    // second control (which question, which field, what value) only appears
    // once the source is remembered, so dropping it would make the choice
    // impossible to complete.
    if (source === 'form') visitor[column] = `@form.${value || ''}`;
    if (source === 'payload') visitor[column] = `@payload.${value || ''}`;
    if (source === 'const') server[column] = { const: value };
    if (source === 'record_id') server[column] = { from: 'record_id' };
    if (source === 'record_field') server[column] = { from: 'record_field', field: value || '' };
    if (source === 'user_id') server[column] = { from: 'user_id' };
    if (source === 'now') server[column] = { from: 'now' };
    if (source === 'user_field') server[column] = { from: 'user_field', field: value || '' };
    if (source === 'ref') server[column] = { from: value || '@' };
    if (source === 'lookup') {
      server[column] = { from: 'lookup', table: '', match_column: '',
                         match_to: { from: 'record_id' }, field: '' };
    }
    // A condition is configured in a dialog, so the stored rule stays as the
    // builder last left it and only Save replaces it. Choosing the source seeds
    // nothing: an incomplete rule the server would refuse must not be reachable
    // by picking an option and navigating away.
    if (source === 'when') {
      const existing = (op.server_fields || {})[column];
      server[column] = existing && existing.from === 'when' ? existing : { from: 'when' };
    }

    // A column that stops being server-filled cannot go on identifying a repeat
    // submission — the server would refuse the whole write as misconfigured.
    // Dropping it here keeps a saved configuration valid by construction.
    const dedupe = (op.dedupe_on || []).filter(c => c in server);

    patchOperation(buttonIndex, opIndex, {
      visitor_fields: visitor,
      server_fields: server,
      dedupe_on: dedupe.length ? dedupe : undefined,
    });
  }

  // The condition is stored fully qualified ("event.status"). The server reads
  // the last segment and the client resolves the whole path, so this one form
  // satisfies both — a bare column name works too, but only by accident.
  // A button's rule is one condition or several, ANDed. `at` names which one is
  // being edited; the stored shape stays a bare object while there is only one,
  // so every existing button round-trips byte-for-byte.
  function conditionList(button) {
    const w = button.visible_when;
    if (Array.isArray(w)) return w;
    return w && w.field ? [w] : [];
  }

  function storeConditions(index, rules) {
    // A field naming no column — `activity.` from a row not yet filled in —
    // resolves to nothing and fails closed, so storing it would hide the button
    // the moment "+ Add a condition" was clicked. An unfinished row stays on
    // screen; it does not become a rule.
    const kept = rules.filter(
      r => r && r.field && String(r.field).split('.').pop() !== '');
    patch(index, {
      visible_when: kept.length === 0 ? undefined
        : kept.length === 1 ? kept[0]
        : kept,
    });
  }

  function writeCondition(index, column, op, value, at = 0) {
    const rules = conditionList(buttons[index]);

    if (!column) {
      // Clearing the only rule removes the condition; clearing one of several
      // removes just that row and leaves the rest enforcing.
      return storeConditions(index, rules.filter((_, i) => i !== at));
    }

    const field = `${tableName || 'record'}.${column}`;

    const nextOp = op || 'eq';
    const next = [...rules];
    next[at] = { field, op: nextOp, value: conditionValue(nextOp, value) };
    settlePending(index, at);
    storeConditions(index, next);
  }

  // Replaces one rule outright. The dialog produces a whole condition rather
  // than a column/op/value triple, because a related rule's field names a table
  // this page's column dropdown does not carry.
  function setConditionRule(index, at, rule) {
    const rules = conditionList(buttons[index]);
    const next = [...rules];
    if (at < next.length) next[at] = rule; else next.push(rule);
    settlePending(index, at);
    storeConditions(index, next);
  }

  function removeCondition(index, at) {
    const rules = conditionList(buttons[index]);
    if (at >= rules.length) {          // a blank row: it was never a rule
      setPendingRows(p => ({ ...p, [index]: Math.max((p[index] || 1) - 1, 0) }));
      return;
    }
    storeConditions(index, rules.filter((_, i) => i !== at));
  }

  function addCondition(index) {
    setPendingRows(p => ({ ...p, [index]: (p[index] || 0) + 1 }));
  }

  /* A blank row stops being blank the moment it is filled in. */
  function settlePending(index, at) {
    if (at >= conditionList(buttons[index]).length) {
      setPendingRows(p => ({ ...p, [index]: Math.max((p[index] || 1) - 1, 0) }));
    }
  }

  // One condition row. Extracted so several can be drawn, and so a row knows
  // which rule it edits — `at` — rather than assuming there is only one.
  function renderConditionRow(button, index, at, total) {
    const c = readCondition(button, at);
    const needsValue = OPS_WITH_VALUE.includes(c.op);

    // A rule may name a column this page does not list — one added to the table
    // after the page was configured, say. Dropping it from the options made the
    // select fall back to "Always show", reporting a real rule as none.
    const columnNames = (recordColumns || []).map(col => col.name);
    const columnOptions = c.column && !columnNames.includes(c.column)
      ? [c.column, ...columnNames]
      : columnNames;

    const allowedValues = allowedValuesOf(recordColumns, c.column);
    const unknownValues = unknownConditionValues(allowedValues, c.value);
    const usePicker = allowedValues.length > 0 && c.op !== 'in';
    const pickerValues = c.value && !allowedValues.some(
      v => v.toLowerCase() === String(c.value).toLowerCase())
      ? [c.value, ...allowedValues]
      : allowedValues;

    // A rule pointing at another table is edited in its own dialog: this page's
    // column dropdown lists only this table's columns, so choosing from it would
    // silently re-point the rule at this table.
    const crossTable = c.field && c.field.split('.').length > 1
      && c.field.split('.')[0] !== (tableName || 'record');

    return (
      <div className="mb-2" key={at}>
        {at > 0 && <div className="small text-muted mb-1">and</div>}
        <div className="d-flex gap-2 flex-wrap align-items-center">
          {crossTable ? (
            // Its own control rather than a dropdown entry: the field names a
            // table this page's column list does not carry, so it is shown as
            // stored and re-opened in the dialog that wrote it.
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary text-start"
              style={{ maxWidth: 320 }}
              aria-label={`Edit related field condition ${at + 1}`}
              onClick={() => setRelatedRule({ index, at })}
            >
              <code>{c.field}</code>{' '}
              {(CONDITION_OPS.find(o => o.key === c.op) || {}).label || c.op}
              {needsValue ? ` ${Array.isArray(c.value) ? c.value.join(', ') : c.value}` : ''}
            </button>
          ) : (
            <select
              // The visible label is attached to the first row; the rest carry
              // their own name, so every row can be addressed rather than only
              // the one the label happens to point at.
              id={at === 0 ? `cond-${index}` : undefined}
              aria-label={at === 0 ? undefined : `Show only when — condition ${at + 1}`}
              className="form-select form-select-sm" style={{ maxWidth: 190 }}
              value={c.column}
              onChange={e => (e.target.value === RELATED_OPTION
                ? setRelatedRule({ index, at })
                : writeCondition(index, e.target.value, c.op, c.value, at))}
            >
              <option value="">Always show</option>
              {columnOptions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
              {hasRelated && (
                <option value={RELATED_OPTION}>A field from a related table…</option>
              )}
            </select>
          )}
          {c.column && !crossTable && (
            <>
              <select
                className="form-select form-select-sm" style={{ maxWidth: 150 }}
                aria-label="Condition operator"
                value={c.op}
                onChange={e => writeCondition(index, c.column, e.target.value, c.value, at)}
              >
                {CONDITION_OPS.map(o => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
              {needsValue && usePicker && (
                <select
                  className="form-select form-select-sm" style={{ maxWidth: 190 }}
                  aria-label="Condition value"
                  value={c.value}
                  onChange={e => writeCondition(index, c.column, c.op, e.target.value, at)}
                >
                  {!c.value && <option value="">— choose a value —</option>}
                  {pickerValues.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              )}
              {needsValue && !usePicker && (
                <input
                  className="form-control form-control-sm" style={{ maxWidth: 170 }}
                  aria-label="Condition value"
                  list={allowedValues.length ? `cond-values-${index}-${at}` : undefined}
                  value={c.value}
                  onChange={e => writeCondition(index, c.column, c.op, e.target.value, at)}
                />
              )}
              {needsValue && !usePicker && allowedValues.length > 0 && (
                <datalist id={`cond-values-${index}-${at}`}>
                  {allowedValues.map(v => <option key={v} value={v} />)}
                </datalist>
              )}
            </>
          )}
          {total > 1 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              // Named, not just an ×: this is the only way to drop a rule that
              // reads through another table, since that row has no column
              // dropdown to blank out.
              aria-label={`Remove condition ${at + 1}`}
              title="Remove this condition"
              onClick={() => removeCondition(index, at)}
            >
              &times;
            </button>
          )}
        </div>
        {!crossTable && needsValue && unknownValues.length > 0 && (
          <div className="small text-danger mt-1">
            {unknownValues.length === 1
              ? <>“{unknownValues[0]}” is not a value </>
              : <>“{unknownValues.join('”, “')}” are not values </>}
            this column holds, so the rule will never match — the button
            simply will not appear, with nothing on screen to say why.
            <div className="mt-1">
              This column holds: <code>{allowedValues.join(', ')}</code>
            </div>
          </div>
        )}
      </div>
    );
  }

  function readCondition(button, at = 0) {
    const w = conditionList(button)[at];
    if (!w || !w.field) return { column: '', op: 'eq', value: '', field: '' };
    return {
      column: String(w.field).split('.').pop(),
      op: w.op || 'eq',
      value: Array.isArray(w.value) ? w.value.join(', ') : (w.value ?? ''),
      field: String(w.field),
    };
  }

  function patchOperation(buttonIndex, opIndex, changes) {
    const ops = [...(buttons[buttonIndex].action?.operations || [])];
    ops[opIndex] = { ...ops[opIndex], ...changes };
    patchAction(buttonIndex, { operations: ops });
  }

  function addOperation(buttonIndex) {
    const ops = [...(buttons[buttonIndex].action?.operations || [])];
    // Each row saved here needs a name later ops can refer to; the order in the
    // list is the order they are written.
    ops.push({ ref: `step${ops.length + 1}`, table: '', visitor_fields: {}, server_fields: {} });
    patchAction(buttonIndex, { operations: ops });
  }

  function removeOperation(buttonIndex, opIndex) {
    const ops = (buttons[buttonIndex].action?.operations || []).filter((_, i) => i !== opIndex);
    patchAction(buttonIndex, { operations: ops });
  }

  // The name an UPDATE operation reads its row id from, or null for an insert.
  // An operation updates when its `id` column is filled — that is how the
  // server tells the two apart — and the page is the only place a visitor's own
  // row id can come from, so only '@payload.<name>' counts as an update here.
  function rowTargetOf(op) {
    const v = (op.visitor_fields || {}).id;
    return typeof v === 'string' && v.startsWith('@payload.')
      ? v.slice('@payload.'.length)
      : null;
  }

  // Whether any value in these operations comes from '@payload.<name>' —
  // a column, a JSON entry, or another operation's row id. `skipIdOf` leaves
  // out one operation's own row id, the one being renamed.
  function readsPayloadName(ops, name, skipIdOf) {
    const wanted = `@payload.${name}`;
    return ops.some((o, i) => {
      const visitor = { ...(o.visitor_fields || {}) };
      if (i === skipIdOf) delete visitor.id;
      return Object.values(visitor).includes(wanted)
        || Object.values(o.json_fields?.map || {}).includes(wanted);
    });
  }

  function ownerChecksOf(button) {
    const rules = button.action?.owner_checked;
    return rules && typeof rules === 'object' && !Array.isArray(rules) ? rules : {};
  }

  /**
   * Switches an operation between adding a row and updating one the page names.
   *
   * THE OWNER CHECK IS WRITTEN IN THE SAME CHANGE. A row id from the page is a
   * number the visitor chose; without the check it is anybody's row. Adding it
   * here rather than leaving it to a second step means there is no moment in
   * which the button is configured to update and not yet guarded. Switching
   * back does not remove the check: another operation may rely on the same
   * name, and a check nobody needs refuses nothing that was allowed.
   */
  function setRowTarget(buttonIndex, opIndex, name) {
    const action = buttons[buttonIndex].action || {};
    const ops = [...(action.operations || [])];
    const op = ops[opIndex];
    const visitor = { ...(op.visitor_fields || {}) };
    delete visitor.id;
    let owner = { ...ownerChecksOf(buttons[buttonIndex]) };
    // A rename MOVES this operation's check rather than adding one per
    // keystroke — typing "pass_id" must not leave "p", "pa", "pas" behind.
    // Only on a rename, and only while nothing else in the button reads the old
    // name: a check that still guards another column must stay.
    const previous = rowTargetOf(op);
    if (name !== null && previous && previous !== name
        && owner[previous] === op.table
        && !readsPayloadName(ops, previous, opIndex)) {
      delete owner[previous];
    }
    if (name !== null) {
      visitor.id = `@payload.${name}`;
      if (name && op.table) owner = { ...owner, [name]: op.table };
    }
    ops[opIndex] = { ...op, visitor_fields: visitor };
    patchAction(buttonIndex, {
      operations: ops,
      owner_checked: Object.keys(owner).length ? owner : undefined,
    });
  }

  // Rebuilt in order so editing one entry does not move it to the end, and an
  // emptied list is dropped entirely rather than stored as {}.
  function writeOwnerChecks(buttonIndex, entries) {
    const next = {};
    entries.forEach(([name, table]) => { next[name] = table; });
    patchAction(buttonIndex, {
      owner_checked: entries.length ? next : undefined,
    });
  }

  function togglePlacement(index, where, on) {
    const current = buttons[index].placement || [];
    patch(index, {
      placement: on ? [...current, where] : current.filter(p => p !== where),
    });
  }

  return (
    <div>
      <div className="small text-muted mb-3">
        Buttons appear where your template says <code>{'{{ actions }}'}</code>. Use{' '}
        <code>{`{{ ${tableName || 'table'}.field }}`}</code> in a label or URL to include
        this record's values.
      </div>

      {buttons.length === 0 && (
        <div className="small text-muted mb-3">No action buttons yet.</div>
      )}

      {buttons.map((button, index) => {
        const action = button.action || {};
        const isSubmit = action.type === 'submit';
        const urlUnsafe = !isSubmit && action.url && !SAFE_URL_RE.test(String(action.url).trim());

        return (
          // Keyed by position, NOT by button.id: the id is typed into this very
          // card, so keying by it rebuilt the card on every keystroke and threw
          // the cursor out of the box. Position is also what every other
          // per-button state here (pendingRows, revealDialogFor) is keyed by.
          <div key={index} className="mb-3 p-2 border rounded"
               style={{ borderColor: '#e2e8f0' }}>
            <div className="mb-2">
              <label className="form-label small fw-semibold mb-1" htmlFor={`ref-${index}`}>
                Reference name
              </label>
              <input
                id={`ref-${index}`}
                className="form-control form-control-sm"
                value={button.id || ''}
                onChange={e => patch(index, { id: e.target.value.trim() })}
              />
              <Hint>
                Place just this button with{' '}
                <code>{`{{ actions.${button.id || 'name'} }}`}</code>, or use{' '}
                <code>{'{{ actions }}'}</code> for all of them.
              </Hint>
            </div>

            <div className="mb-2">
              <label className="form-label small fw-semibold mb-1" htmlFor={`lbl-${index}`}>
                Label
              </label>
              <input
                id={`lbl-${index}`}
                className="form-control form-control-sm"
                value={button.label || ''}
                onChange={e => patch(index, { label: e.target.value })}
              />
            </div>

            <div className="mb-2">
              <label className="form-label small fw-semibold mb-1" htmlFor={`act-${index}`}>
                When clicked
              </label>
              <select
                id={`act-${index}`}
                className="form-select form-select-sm"
                value={action.type || 'redirect'}
                onChange={e => patchAction(index, { type: e.target.value })}
              >
                <option value="redirect">Go to a link or page</option>
                <option value="submit">Save a record (with or without a form)</option>
              </select>
            </div>

            {(() => {
              // One row per condition, ANDed. A button with a single rule shows
              // exactly one row and stores a bare object, so nothing that exists
              // today changes shape. `at` is which rule a control edits.
              const rules = conditionList(button);
              const total = Math.max(rules.length + (pendingRows[index] || 0), 1);
              const rows = Array.from({ length: total }, (_, i) => i);
              return (
                <div className="mb-2">
                  <label className="form-label small fw-semibold mb-1" htmlFor={`cond-${index}`}>
                    Show only when
                  </label>
                  {rows.map(at => renderConditionRow(button, index, at, rows.length))}
                  <button
                    type="button"
                    className="btn btn-sm btn-link px-0"
                    onClick={() => addCondition(index)}
                  >
                    + Add a condition
                  </button>
                  <Hint>
                    Every condition must hold. Hidden buttons are also refused by
                    the server, so this is a real rule, not just presentation.
                  </Hint>
                </div>
              );
            })()}


            <div className="d-flex gap-3 align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input" type="checkbox" id={`pd-${index}`}
                  checked={(button.placement || []).includes('detail')}
                  onChange={e => togglePlacement(index, 'detail', e.target.checked)}
                />
                <label className="form-check-label small" htmlFor={`pd-${index}`}>
                  Show on detail page
                </label>
              </div>
              {/*
                * Enabled now that a custom LIST layout renders cards from a
                * template — a card-placed button appears wherever the template
                * puts <div data-arcee-actions="<id>">.
                *
                * It stays meaningless for the BUILT-IN card grid, which draws
                * its own markup and has no anchor to render into. That is why
                * the hint below says where the button will actually show up,
                * rather than the checkbox being disabled and saying nothing.
                */}
              <div className="form-check">
                <input
                  className="form-check-input" type="checkbox" id={`pc-${index}`}
                  checked={(button.placement || []).includes('card')}
                  onChange={e => togglePlacement(index, 'card', e.target.checked)}
                />
                <label className="form-check-label small" htmlFor={`pc-${index}`}>
                  Show on cards
                </label>
              </div>
            </div>

            {(button.placement || []).includes('card') && (
              <Hint>
                Cards only show buttons when the list page uses a custom layout.
                Add <code>{`<div data-arcee-actions="${button.id || 'name'}"></div>`}</code>
                {' '}to that template where the button should appear.
              </Hint>
            )}

            {!isSubmit ? (
              <div className="mb-2">
                <label className="form-label small fw-semibold mb-1" htmlFor={`url-${index}`}>
                  Destination URL
                </label>
                <input
                  id={`url-${index}`}
                  className={`form-control form-control-sm ${urlUnsafe ? 'is-invalid' : ''}`}
                  placeholder="https://pay.example.com/?id={{ record.id }}"
                  value={action.url || ''}
                  onChange={e => patchAction(index, { url: e.target.value })}
                />
                {urlUnsafe && (
                  <div className="form-text text-danger">
                    Must start with https://, http://, mailto:, tel: or / — other links
                    are refused and the button will not render.
                  </div>
                )}
                <div className="form-check mt-1">
                  <input
                    className="form-check-input" type="checkbox"
                    id={`tab-${index}`}
                    checked={Boolean(action.open_in_new_tab)}
                    onChange={e => patchAction(index, { open_in_new_tab: e.target.checked })}
                  />
                  <label className="form-check-label small" htmlFor={`tab-${index}`}>
                    Open in a new tab
                  </label>
                </div>
              </div>
            ) : (
              <div className="mb-2">
                <label className="form-label small fw-semibold mb-1" htmlFor={`frm-${index}`}>
                  Form to open
                </label>
                <select
                  id={`frm-${index}`}
                  className="form-select form-select-sm"
                  value={action.form_id || ''}
                  onChange={e => patchAction(index, {
                    form_id: e.target.value ? Number(e.target.value) : null,
                  })}
                >
                  <option value="">— Choose a form —</option>
                  {availableForms.map(f => (
                    <option key={f.id} value={f.id}>{f.title}</option>
                  ))}
                </select>
                {/* A page that collects its own values (a check-in panel, say)
                    needs no form, so a form is only demanded of a button that
                    has no other way to receive anything. */}
                {!action.form_id && (
                  JSON.stringify(action.operations || []).includes('"@payload.') ? (
                    <Hint>No form — this button takes its values from the page.</Hint>
                  ) : (
                    <div className="form-text text-danger">
                      Choose a form, or this button has nothing to collect.
                    </div>
                  )
                )}
                <Hint>
                  Build the fields in Page Builder → Form Builder. Place the form in
                  your layout with <code>{`{{ actions.${button.id || 'name'} }}`}</code>
                  {' '}or <code>{`<div data-arcee-form="${button.id || 'name'}"></div>`}</code>.
                </Hint>

                <div className="small fw-semibold mt-3 mb-1">Saves into</div>
                {(action.operations || []).length === 0 && (
                  <div className="form-text text-danger mb-2">
                    No tables yet — add at least one.
                  </div>
                )}

                {(action.operations || []).map((op, oi) => (
                  <div key={oi} className="mb-2">
                    <div className="d-flex gap-2 align-items-center">
                    <span className="badge bg-secondary">{oi + 1}</span>
                    <select
                      className="form-select form-select-sm"
                      aria-label={`Table ${oi + 1}`}
                      value={op.table || ''}
                      onChange={e => patchOperation(index, oi, { table: e.target.value })}
                    >
                      <option value="">— Choose a table —</option>
                      {tables.map(t => (
                        <option key={t.name} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                    <button
                      type="button" className="btn btn-sm btn-outline-danger"
                      onClick={() => removeOperation(index, oi)}
                    >
                      Remove
                    </button>
                    </div>

                    {op.table && (
                      <div className="ms-4 mb-3">
                        {(() => {
                          const target = rowTargetOf(op);
                          const guarded = target
                            && ownerChecksOf(button)[target] === op.table;
                          return (
                            <div className="mt-2 mb-2">
                              <div className="d-flex gap-2 align-items-center">
                                <span className="small fw-semibold" style={{ minWidth: 110 }}>
                                  Row to write
                                </span>
                                <select
                                  className="form-select form-select-sm" style={{ maxWidth: 260 }}
                                  aria-label={`Row to write ${oi + 1}`}
                                  value={target === null ? 'new' : 'named'}
                                  onChange={e => setRowTarget(index, oi,
                                    e.target.value === 'named' ? '' : null)}
                                >
                                  <option value="new">Add a new row</option>
                                  <option value="named">Update a row the page names</option>
                                </select>
                              </div>
                              {target !== null && (
                                <>
                                  <div className="d-flex gap-2 align-items-center mt-1">
                                    <span className="small" style={{ minWidth: 110 }}>
                                      Row id sent as
                                    </span>
                                    <input
                                      className="form-control form-control-sm"
                                      style={{ maxWidth: 260 }}
                                      aria-label={`Row id name ${oi + 1}`}
                                      placeholder="name the page sends, e.g. pass_id"
                                      value={target}
                                      onChange={e => setRowTarget(index, oi, e.target.value.trim())}
                                    />
                                  </div>
                                  {target && !guarded ? (
                                    <div className="form-text text-danger">
                                      Not protected: anyone could send another person's
                                      id. Add <code>{target}</code> → <code>{op.table}</code>
                                      {' '}under "Only the visitor's own rows" below.
                                    </div>
                                  ) : (
                                    <Hint>
                                      Only the columns filled in below are changed; the rest
                                      of the row stays as it is. The server checks the row
                                      belongs to the signed-in visitor before saving.
                                    </Hint>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}
                        <div className="small text-muted mb-1">
                          Where each column's value comes from — leave a row blank to skip it.
                        </div>
                        {(mappableColumnsOf(op.table)).map(col => {
                          const m = readMapping(op, col.name);
                          // The values this column declares, if any. A fixed value
                          // typed by hand is the other silent mistake on this screen:
                          // the column is a plain varchar with no CHECK constraint, so
                          // a misspelt status is stored without complaint and only
                          // surfaces when something downstream fails to recognise it.
                          const colValues = allowedValuesOf([col], col.name);
                          const constPicker = m.source === 'const' && colValues.length > 0;

                          // A column the platform mints at insert. Shown as such
                          // rather than as an unfilled source picker, which reads
                          // identically to a column nobody got round to and invites
                          // mapping a value over one guaranteed to be unique.
                          //
                          // Only when nothing is mapped. An existing mapping stays
                          // visible and editable — hiding it would leave a value
                          // configured that no screen admits to, which is worse than
                          // the trap this replaces.
                          const generatedBy = col.generation_strategy;
                          const showGenerated = !!generatedBy && !m.source;
                          // A stored value the column no longer declares stays
                          // selectable, so opening this panel cannot quietly rewrite
                          // it to the first option.
                          const constOptions = m.value && !colValues.some(
                            v => v.toLowerCase() === String(m.value).toLowerCase())
                            ? [m.value, ...colValues]
                            : colValues;
                          return (
                            <div className="mb-1" key={col.name}>
                            <div className="d-flex gap-2 align-items-center">
                              <code style={{ minWidth: 110, wordBreak: 'break-all' }}>{col.name}</code>
                              {showGenerated ? (
                                <span className="badge bg-light text-secondary border fw-normal"
                                      style={{ fontSize: 12, padding: '6px 10px' }}
                                      title="Filled in by the platform when the row is created.">
                                  Generated automatically ({generatedBy})
                                </span>
                              ) : (
                              <select
                                className="form-select form-select-sm" style={{ maxWidth: 210 }}
                                aria-label={`Source for ${col.name}`}
                                value={m.source}
                                onChange={e => writeMapping(index, oi, col.name, e.target.value, '')}
                              >
                                <option value="">— not set —</option>
                                {SOURCES.map(sc => (
                                  <option key={sc.key} value={sc.key}>{sc.label}</option>
                                ))}
                                {/* A source that is no longer offered but was
                                    saved earlier. Without this the select would
                                    render blank and the next save would discard
                                    a value the builder never chose to remove. */}
                                {m.source && !SOURCES.some(sc => sc.key === m.source) && (
                                  <option value={m.source}>
                                    {m.source} (no longer offered)
                                  </option>
                                )}
                              </select>
                              )}
                              {(m.source === 'form' || m.source === 'record_field'
                                || m.source === 'const' || m.source === 'ref'
                                || m.source === 'user_field' || m.source === 'payload') && (
                                m.source === 'form' ? (
                                  <select
                                    className="form-select form-select-sm" style={{ maxWidth: 200 }}
                                    aria-label={`Form field for ${col.name}`}
                                    value={m.value}
                                    onChange={e => writeMapping(index, oi, col.name, 'form', e.target.value)}
                                  >
                                    <option value="">— choose a question —</option>
                                    {formFieldsOf(action.form_id).map(f => (
                                      <option key={f.id} value={f.id}>{f.label || f.id}</option>
                                    ))}
                                  </select>
                                ) : m.source === 'record_field' ? (
                                  /* A picker, not free text: a column name that
                                     does not exist resolves to null at submit
                                     time with nothing to show for it. */
                                  <select
                                    className="form-select form-select-sm" style={{ maxWidth: 200 }}
                                    aria-label={`Record column for ${col.name}`}
                                    value={m.value}
                                    onChange={e => writeMapping(index, oi, col.name, 'record_field', e.target.value)}
                                  >
                                    <option value="">— choose a column —</option>
                                    {(recordColumns || []).map(c => (
                                      <option key={c.name} value={c.name}>{c.name}</option>
                                    ))}
                                  </select>
                                ) : constPicker ? (
                                  /* A picker for the same reason record_field is one:
                                     the column already says what it accepts, so there
                                     is nothing to gain from letting someone type it
                                     from memory. */
                                  <select
                                    className="form-select form-select-sm" style={{ maxWidth: 200 }}
                                    aria-label={`Value for ${col.name}`}
                                    value={m.value}
                                    onChange={e => writeMapping(index, oi, col.name, 'const', e.target.value)}
                                  >
                                    {!m.value && <option value="">— choose a value —</option>}
                                    {constOptions.map(v => (
                                      <option key={v} value={v}>{v}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    className="form-control form-control-sm" style={{ maxWidth: 200 }}
                                    aria-label={`Value for ${col.name}`}
                                    placeholder={m.source === 'ref' ? '@step1'
                                      : m.source === 'payload' ? PAYLOAD_PLACEHOLDER : ''}
                                    value={m.value}
                                    onChange={e => writeMapping(index, oi, col.name, m.source, e.target.value)}
                                  />
                                )
                              )}
                            </div>
                            {/* Full width on its own line: five controls cannot
                                sit beside the source picker in a narrow panel. */}
                            {m.source === 'lookup' && (
                              <LookupFields
                                column={col.name}
                                value={m.lookup}
                                tables={tables}
                                columnsOf={columnsOf}
                                matchSources={MATCH_SOURCES}
                                onChange={changes => writeLookup(index, oi, col.name, changes)}
                              />
                            )}
                            {/* A condition has four controls per branch, so it
                                is configured in a dialog and the row grows by
                                one summary line — the same trade the pass
                                reveal makes. */}
                            {m.source === 'when' && (
                              <div className="mt-1 mb-2 p-2 rounded border bg-body-tertiary
                                              d-flex align-items-center gap-2">
                                <div className="small text-muted flex-grow-1">
                                  {summariseCondition(m.when)}
                                </div>
                                <button
                                  type="button" className="btn btn-sm btn-primary flex-shrink-0"
                                  onClick={() => setConditionFor(
                                    { buttonIndex: index, opIndex: oi, column: col.name })}
                                >
                                  Configure
                                </button>
                              </div>
                            )}
                            </div>
                          );
                        })}

                        {/* One row per person: the upsert key.

                            HIDDEN unless already in use. This app leaves duplicate
                            registrations to a partial unique index on the table,
                            which refuses a second row outright, so the control was
                            one more decision on an already long panel.

                            It is NOT the same thing, and that is why it still renders
                            whenever a value is set: the index REFUSES a repeat, while
                            this UPDATES the row that exists — which is what a
                            repeatable action (an RSVP that flips, a preference, a
                            toggle) actually wants. A configured value must never
                            become invisible; that leaves config no screen admits to. */}
                        {serverColumnsOf(op).length > 0 && (op.dedupe_on || []).length > 0 && (
                          <div className="mt-2 pt-2 border-top">
                            <div className="small fw-semibold mb-1">
                              Only one row per…
                            </div>
                            <div className="d-flex gap-3 flex-wrap">
                              {serverColumnsOf(op).map(column => (
                                <div className="form-check" key={column}>
                                  <input
                                    className="form-check-input" type="checkbox"
                                    id={`dedupe-${index}-${oi}-${column}`}
                                    checked={(op.dedupe_on || []).includes(column)}
                                    onChange={e => toggleDedupe(index, oi, column, e.target.checked)}
                                  />
                                  <label className="form-check-label small"
                                         htmlFor={`dedupe-${index}-${oi}-${column}`}>
                                    <code>{column}</code>
                                  </label>
                                </div>
                              ))}
                            </div>
                            <Hint>
                              {(op.dedupe_on || []).length === 0
                                ? 'Nothing ticked — every click adds another row, so someone '
                                  + 'who comes back registers twice.'
                                : `A repeat submission with the same ${(op.dedupe_on || []).join(' + ')} `
                                  + 'updates that row instead of adding another.'}
                              {' '}Only server-filled columns can be used, because the visitor
                              must not be able to aim this at someone else's row.
                            </Hint>
                          </div>
                        )}
                        {/* Extra values into a JSON column. Offered only
                            where there is a column to put them in. */}
                        {jsonColumnsOf(op.table).length > 0 && (
                          <JsonFieldMap
                            op={op}
                            columns={jsonColumnsOf(op.table)}
                            formFields={formFieldsOf(action.form_id)}
                            recordColumns={recordColumns}
                            sources={JSON_SOURCES}
                            idPrefix={`jf-${index}-${oi}`}
                            onChange={changes => patchOperation(index, oi, changes)}
                          />
                        )}

                      </div>
                    )}
                  </div>
                ))}

                <button
                  type="button" className="btn btn-sm btn-outline-secondary"
                  onClick={() => addOperation(index)}
                >
                  + Add table
                </button>

                <Hint>
                  Tables are saved in order, so a parent comes before anything that
                  refers to it. All of them save together, or none do. Visitors sign in
                  first, and the record and any amounts are filled in by the server —
                  never sent by the browser.
                </Hint>

                {/* action.owner_checked: page-sent values that name a row, each
                    proved to belong to the signed-in visitor before anything is
                    written. Kept beside the tables because it is about them. */}
                {(() => {
                  const entries = Object.entries(ownerChecksOf(button));
                  const replace = (at, pair) => writeOwnerChecks(index,
                    entries.map((e, i) => (i === at ? pair : e)));
                  return (
                    <div className="mt-3">
                      <div className="small fw-semibold mb-1">Only the visitor's own rows</div>
                      {entries.map(([name, table], at) => (
                        <div className="d-flex gap-2 align-items-center mb-1" key={at}>
                          <input
                            className="form-control form-control-sm" style={{ maxWidth: 160 }}
                            aria-label={`Owner check name ${at + 1}`}
                            placeholder="e.g. pass_id"
                            value={name}
                            onChange={e => replace(at, [e.target.value.trim(), table])}
                          />
                          <span className="small text-muted">must be the visitor's row in</span>
                          <select
                            className="form-select form-select-sm" style={{ maxWidth: 200 }}
                            aria-label={`Owner check table ${at + 1}`}
                            value={table || ''}
                            onChange={e => replace(at, [name, e.target.value])}
                          >
                            <option value="">— Choose a table —</option>
                            {table && !tables.some(t => t.name === table) && (
                              <option value={table}>{table}</option>
                            )}
                            {tables.map(t => (
                              <option key={t.name} value={t.name}>{t.name}</option>
                            ))}
                          </select>
                          <button
                            type="button" className="btn btn-sm btn-outline-danger"
                            aria-label={`Remove owner check ${at + 1}`}
                            onClick={() => writeOwnerChecks(index,
                              entries.filter((_, i) => i !== at))}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button" className="btn btn-sm btn-outline-secondary"
                        // A blank name is not stored until typed — an object
                        // cannot hold two of them, and the server would look
                        // the empty key up and find nothing to check.
                        disabled={entries.some(([name]) => !name)}
                        onClick={() => writeOwnerChecks(index, [...entries, ['', '']])}
                      >
                        + Add owner check
                      </button>
                      <Hint>
                        For a value sent by the page that names a row — "check in
                        THIS seat". The server looks the row up and refuses the
                        whole save unless the signed-in visitor owns it.
                      </Hint>
                    </div>
                  );
                })()}

                <div className="small fw-semibold mt-3 mb-1">After it saves</div>

                <label className="form-label small fw-semibold mb-1" htmlFor={`msg-${index}`}>
                  Success message
                </label>
                <div className="form-text mt-0 mb-1">
                  Can include a column of a row this button saves, like
                  <code className="mx-1">{'{{ table.column }}'}</code>.
                </div>
                <input
                  id={`msg-${index}`}
                  className="form-control form-control-sm"
                  value={action.on_success?.message || ''}
                  placeholder="Thank you — that is saved."
                  onChange={e => patchOnSuccess(index, { message: e.target.value })}
                />

                <div className="form-check mt-3">
                  <input
                    className="form-check-input" type="checkbox"
                    id={`done-${index}`}
                    checked={action.on_completed?.hide === true}
                    onChange={e => patchOnCompleted(index, {
                      hide: e.target.checked ? true : undefined,
                    })}
                  />
                  <label className="form-check-label small" htmlFor={`done-${index}`}>
                    Hide this button once the visitor has completed it
                  </label>
                  <div className="form-text">
                    The message above takes its place, until they sign out.
                  </div>
                </div>

                <div className="form-check mt-2">
                  <input
                    className="form-check-input" type="checkbox"
                    id={`rev-${index}`}
                    aria-label="Show a pass or receipt afterwards"
                    // Reads as on while the dialog is open too. Nothing is
                    // stored until Save, so binding purely to the stored value
                    // makes the switch spring back the instant it is clicked.
                    checked={Boolean(action.on_success?.reveal) || revealDialogFor === index}
                    onChange={e => {
                      if (e.target.checked) setRevealDialogFor(index);
                      else patchOnSuccess(index, { reveal: undefined });
                    }}
                  />
                  <label className="form-check-label small" htmlFor={`rev-${index}`}>
                    Show a pass or receipt afterwards
                  </label>
                </div>

                {action.on_success?.reveal ? (
                  <div className="d-flex align-items-center gap-2 border rounded p-2 mt-2">
                    <div className="small text-muted flex-grow-1">
                      <span className="fw-semibold text-body">
                        {action.on_success.reveal.table}
                      </span>
                      {' · '}
                      {(action.on_success.reveal.fields || []).length} field
                      {(action.on_success.reveal.fields || []).length === 1 ? '' : 's'} as text
                      {' · '}
                      {action.on_success.reveal.qr?.column
                        ? <>QR from <code>{action.on_success.reveal.qr.column}</code></>
                        : 'no QR'}
                    </div>
                    <button
                      type="button" className="btn btn-sm btn-primary"
                      onClick={() => setRevealDialogFor(index)}
                    >
                      Configure
                    </button>
                  </div>
                ) : (
                  <Hint>
                    Off means today's behaviour: a plain one-line confirmation.
                  </Hint>
                )}

                {revealDialogFor === index && (
                  <SuccessRevealDialog
                    button={button}
                    columnsOf={columnsOf}
                    reveal={action.on_success?.reveal}
                    onCancel={() => setRevealDialogFor(null)}
                    onSave={reveal => {
                      patchOnSuccess(index, { reveal });
                      setRevealDialogFor(null);
                    }}
                  />
                )}
              </div>
            )}

            {conditionFor && conditionFor.buttonIndex === index && (
              <ConditionRuleDialog
                column={conditionFor.column}
                rule={((action.operations || [])[conditionFor.opIndex] || {})
                        .server_fields?.[conditionFor.column]}
                tables={tables}
                columnsOf={columnsOf}
                recordColumns={recordColumns}
                matchSources={MATCH_SOURCES}
                onCancel={() => setConditionFor(null)}
                onSave={rule => {
                  const op = (action.operations || [])[conditionFor.opIndex] || {};
                  const server = { ...(op.server_fields || {}) };
                  server[conditionFor.column] = rule;
                  patchOperation(index, conditionFor.opIndex, { server_fields: server });
                  setConditionFor(null);
                }}
              />
            )}

            <div className="d-flex align-items-center mt-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-danger ms-auto"
                onClick={() => write(buttons.filter((_, i) => i !== index))}
              >
                Remove
              </button>
            </div>

          </div>
        );
      })}

      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={() => {
          const base = newButton();
          const taken = new Set(buttons.map(b => b.id));
          let id = base.id, n = 2;
          while (taken.has(id)) { id = `${base.id}${n++}`; }
          write([...buttons, { ...base, id, order: buttons.length }]);
        }}
      >
        + Add button
      </button>

      {relatedRule && (
        <RelatedFieldRuleDialog
          rule={conditionList(buttons[relatedRule.index])[relatedRule.at]}
          relatedEdges={relatedEdges}
          onSave={rule => {
            setConditionRule(relatedRule.index, relatedRule.at, rule);
            setRelatedRule(null);
          }}
          onCancel={() => setRelatedRule(null)}
        />
      )}
    </div>
  );
}
